/**
 * Roll Fast TMS — Enterprise Information Architecture (v3.1 · Feb 2026).
 *
 * SIX top-level workspaces · 18 sidebar destinations · tabbed hubs
 * for deep functionality (Samsara / Motive / Fleetio / HubSpot style).
 *
 * Every legacy route in App.js still resolves — either directly (if
 * the sub-page lives inside a hub) or via <Navigate> redirect (if the
 * flat path was retired from the sidebar). Deep links never 404.
 */
import {
  Truck, MapPin, Robot, ListBullets, UsersFour, Gauge, Gear,
  ChartLineUp, ShieldCheck, UserPlus, CreditCard, Phone,
  BookOpen, Wrench, Users, Bell, ChartBar,
  Lifebuoy,
} from "@phosphor-icons/react";


export const WORKSPACES = [
  // ───────────────────────────────────────────────────────────────────
  // 1. Dashboard
  // ───────────────────────────────────────────────────────────────────
  {
    id: "dashboard",
    label: "Dashboard",
    shortLabel: "Dash",
    icon: Gauge,
    defaultOpen: true,
    description: "Landing view — Command Center, operational KPIs, and activity.",
    items: [
      { to: "/admin/command",  label: "Command Center",    icon: Gauge,       testid: "nav-command",
        aliases: ["dashboard", "home"] },
      { to: "/admin/kpis",     label: "Operations KPIs",   icon: ChartLineUp, testid: "nav-kpis",
        aliases: ["kpis", "ops metrics"] },
      { to: "/admin/activity", label: "Activity Timeline", icon: Bell,        testid: "nav-activity",
        aliases: ["events", "notifications", "audit timeline"] },
    ],
  },

  // ───────────────────────────────────────────────────────────────────
  // 2. Operations — carrier-first (iter-139)
  // ───────────────────────────────────────────────────────────────────
  {
    id: "operations",
    label: "Operations",
    shortLabel: "Ops",
    icon: Truck,
    defaultOpen: true,
    description: "Dispatch, carriers, drivers, and compliance workflows.",
    items: [
      { to: "/admin/operations/dispatch",   label: "Dispatch",   icon: MapPin,      testid: "nav-ops-dispatch",
        aliases: ["loads", "load board", "live map", "check calls", "load sourcing", "ai load booking"] },
      { to: "/admin/operations/carriers",   label: "Carriers",   icon: Truck,       testid: "nav-ops-carriers",
        aliases: ["carrier directory", "application review", "carrier documents", "carrier performance", "carrier profile", "vehicles", "equipment", "trucks", "brokers", "referrals"] },
      { to: "/admin/operations/drivers",    label: "Drivers",    icon: UsersFour,   testid: "nav-ops-drivers",
        aliases: ["drivers", "dispatchers"] },
      { to: "/admin/operations/compliance", label: "Compliance", icon: ShieldCheck, testid: "nav-ops-compliance",
        aliases: ["compliance", "ifta", "audit log", "security", "2fa", "carrier onboarding"] },
    ],
  },

  // ───────────────────────────────────────────────────────────────────
  // 3. Recruiting — single workspace with 5 nested tabs
  // ───────────────────────────────────────────────────────────────────
  {
    id: "recruiting",
    label: "Recruiting",
    shortLabel: "Recruit",
    icon: UserPlus,
    description: "Autonomous carrier recruiting — pipeline, campaigns, applicants, analytics, operations.",
    items: [
      { to: "/admin/recruiting",  label: "Workspace",  icon: UserPlus,   testid: "nav-recruiting-workspace", adminOnly: true,
        aliases: ["pipeline", "carrier discovery", "prospect journey", "prospect timeline", "campaigns", "outreach sequences", "recruiter settings", "needs info queue", "follow up queue", "applicants", "applications", "voice applications", "leads", "analytics", "recruiter kpis", "follow-up kpis", "ai scoring", "scoring weights", "operations", "recruiting ops"] },
    ],
  },

  // ───────────────────────────────────────────────────────────────────
  // 4. AI Center — 5 hubs
  // ───────────────────────────────────────────────────────────────────
  {
    id: "ai_center",
    label: "AI Center",
    shortLabel: "AI",
    icon: Robot,
    description: "AI Receptionist agents, calls, analytics, knowledge, and configuration.",
    items: [
      { to: "/admin/ai/agents",        label: "Agents",         icon: Robot,       testid: "nav-ai-agents",
        aliases: ["ai control", "autonomous", "personas", "operational reminders", "ai follow-ups"] },
      { to: "/admin/ai/calls",         label: "Calls",          icon: Phone,       testid: "nav-ai-calls", badgeKey: "voice_active",
        aliases: ["live calls", "call history", "voice actions", "transfer routing"] },
      { to: "/admin/ai/analytics",     label: "Analytics",      icon: ChartBar,    testid: "nav-ai-analytics",
        aliases: ["voice analytics", "receptionist kpis", "ai briefs", "ai pricing"] },
      { to: "/admin/ai/knowledge",     label: "Knowledge Base", icon: BookOpen,    testid: "nav-ai-knowledge", adminOnly: true,
        aliases: ["knowledge manager"] },
      { to: "/admin/ai/configuration", label: "Voice Engine",  icon: Gear,        testid: "nav-ai-configuration", adminOnly: true,
        aliases: ["voice config", "voice provider", "voice assignments", "transfer routing", "health & alerts", "system health", "monitor", "configuration"] },
    ],
  },

  // ───────────────────────────────────────────────────────────────────
  // 5. Finance — single hub with 6 tabs
  // ───────────────────────────────────────────────────────────────────
  {
    id: "finance",
    label: "Finance",
    shortLabel: "Finance",
    icon: CreditCard,
    description: "Invoices, settlements, payroll, profitability, factoring, and payment preferences.",
    items: [
      { to: "/admin/finance", label: "Finance", icon: CreditCard, testid: "nav-finance",
        aliases: ["invoices", "settlements", "payroll", "payment preferences", "profitability", "factoring"] },
    ],
  },

  // ───────────────────────────────────────────────────────────────────
  // 6. Settings — single hub with 10 tabs
  // ───────────────────────────────────────────────────────────────────
  {
    id: "settings",
    label: "Settings",
    shortLabel: "Settings",
    icon: Gear,
    adminOnly: true,
    description: "Integrations, API keys, users, branding, data, and deployment.",
    items: [
      { to: "/admin/settings-hub", label: "Settings", icon: Gear, testid: "nav-settings", adminOnly: true,
        aliases: ["integration center", "api keys", "edi", "users", "general settings",
                   "branding", "data management", "activity log", "launch wizard",
                   "deployment readiness", "settings"] },
    ],
  },
];


/**
 * Resolve the workspace + item metadata for a pathname.
 * Uses `prefix` matching so nested hub sub-routes light up the correct
 * top-level sidebar item (e.g. /admin/finance/invoices → Finance).
 */
export function resolveActive(pathname) {
  for (const ws of WORKSPACES) {
    for (const it of ws.items) {
      const match = it.exact
        ? pathname === it.to
        : pathname === it.to || pathname.startsWith(it.to + "/");
      if (match) return { workspace: ws, item: it };
    }
  }
  return { workspace: null, item: null };
}


/**
 * Flat list of every navigable item across all workspaces — used by
 * the command palette (⌘K) for fast fuzzy search.
 */
export function flatNavItems(isAdmin = true) {
  const rows = [];
  for (const ws of WORKSPACES) {
    if (ws.adminOnly && !isAdmin) continue;
    for (const it of ws.items) {
      if (it.adminOnly && !isAdmin) continue;
      rows.push({
        to: it.to,
        label: it.label,
        workspace: ws.label,
        workspaceId: ws.id,
        icon: it.icon,
        aliases: it.aliases || [],
        testid: it.testid,
      });
    }
  }
  return rows;
}


// Re-export the icon used by the sidebar footer so consumers don't need
// to keep a second phosphor import chain in sync.
export { Lifebuoy };
